/* ================================
   student-price-fix.js — DISPLAY ONLY

   - No math
   - No cents parsing
   - Only shows student price if:
       cookie student_mode=1 exists
       AND .price__student[data-student-price] exists
   - Adds .student-active to .price when student is active (for strike-through CSS)
   - Re-runs on:
       DOM ready
       variant changes (Shopify + Dawn)
       WPD re-renders (MutationObserver)
       custom "student:unlocked" event (from student-mode.js)
   ================================ */

(() => {
  "use strict";

  const COOKIE_NAME = "student_mode";
  const COOKIE_VALUE = "1";

  function hasStudentCookie() {
    return document.cookie.split(";").some((c) => c.trim() === `${COOKIE_NAME}=${COOKIE_VALUE}`);
  }

  function isCustomerLoggedIn() {
    return (
      document.documentElement.getAttribute("data-customer-logged-in") === "true" ||
      document.body?.classList.contains("customer-logged-in")
    );
  }

  function applyStudentPrices(root = document) {
    // Student prices must never display to a logged-out visitor just because
    // an old student_mode cookie/localStorage value is still present.
    const isStudent = hasStudentCookie() && isCustomerLoggedIn();

    root.querySelectorAll(".price").forEach((priceEl) => {
      const studentEl = priceEl.querySelector(".price__student[data-student-price]");
      const val = studentEl ? (studentEl.getAttribute("data-student-price") || "").trim() : "";
      const hasStudentPrice = !!studentEl && val !== "";

      if (isStudent && hasStudentPrice) {
        studentEl.hidden = false;
        priceEl.classList.add("student-active");
      } else {
        if (studentEl) studentEl.hidden = true;
        priceEl.classList.remove("student-active");
      }
    });
  }

  function refresh() {
    applyStudentPrices(document);
  }

  // Run once on load
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", refresh);
  } else {
    refresh();
  }

  // Run after unlock (immediate refresh without page reload)
  window.addEventListener("student:unlocked", () => {
    setTimeout(refresh, 25);
    setTimeout(refresh, 250);
  });

  // Variant changes (Dawn often uses these)
  document.addEventListener("variant:change", () => {
    setTimeout(refresh, 25);
    setTimeout(refresh, 250);
  });

  // Some themes fire this on product updates
  document.addEventListener("product:variant-change", () => {
    setTimeout(refresh, 25);
    setTimeout(refresh, 250);
  });

  // Watch for WPD / AJAX re-renders
  const mo = new MutationObserver(() => {
    // quick debounce-ish behaviour by scheduling refresh
    setTimeout(refresh, 25);
  });

  mo.observe(document.documentElement, { childList: true, subtree: true });

  // Expose manual debug hook
  window.StudentPriceFix = window.StudentPriceFix || {};
  window.StudentPriceFix.refresh = refresh;
})();

/* ================================
   Student announcement bar (SAFER + handles rotating bars)
   - No MutationObserver
   - Runs on load + student:unlocked
   - Updates ALL announcement links/messages (in case of sliders)
   ================================ */
(function () {
  const COOKIE = "student_mode=1";
  const APPLIED_ATTR = "data-student-announcement";

  function hasStudentCookie() {
    return document.cookie.includes(COOKIE);
  }

  function setMessageKeepIcons(messageEl, newText) {
    if (!messageEl) return;

    // If there's a span we can replace, do it
    const span = messageEl.querySelector("span");
    if (span) {
      span.textContent = newText;
      return;
    }

    // Otherwise, replace the first text node only (keeps arrows/icons intact)
    for (const node of messageEl.childNodes) {
      if (node.nodeType === Node.TEXT_NODE) {
        node.nodeValue = newText + " ";
        return;
      }
    }

    // Fallback: set textContent (may remove icons if none exist separately)
    messageEl.textContent = newText;
  }

  function apply() {
    const isStudent = hasStudentCookie() && (
      document.documentElement.getAttribute("data-customer-logged-in") === "true" ||
      document.body?.classList.contains("customer-logged-in")
    );

    // Announcement styling is only active for a signed-in student.
    // The registration/login pages can still retain the cookie without
    // exposing student pricing or student storefront messaging.
    if (isStudent) document.documentElement.classList.add("student-mode");

    if (!isStudent) return;

    // Update ALL announcement bar links (handles slider/rotation)
    const links = document.querySelectorAll(".announcement-bar__link");
    if (!links.length) return;

    links.forEach((link) => {
      if (link.getAttribute(APPLIED_ATTR) === "1") return;

      // Change link target
      link.href = "/collections/all";

      // Change message text (keep arrow icons)
      const msg = link.querySelector(".announcement-bar__message");
      setMessageKeepIcons(
        msg,
        "🎓 Welcome MC Students! Student pricing is live — shop now!"
      );

      link.setAttribute(APPLIED_ATTR, "1");
    });
  }

  // Run on load
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", apply);
  } else {
    apply();
  }

  // Run immediately after unlock
  window.addEventListener("student:unlocked", () => {
    setTimeout(apply, 25);
    setTimeout(apply, 250);
  });

  // Also run shortly after load to catch announcement slider init
  window.addEventListener("load", () => {
    setTimeout(apply, 250);
    setTimeout(apply, 1000);
  });
})();
